import React, { useState, useEffect, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import MelaAppBanner from './components/MelaAppBanner';
import MelaBridgeIndicator from './components/MelaBridgeIndicator';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { HelmetProvider } from 'react-helmet-async';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { BrandProvider } from './contexts/BrandContext';
import './App.css';
import ErrorBoundary from './components/ErrorBoundary';
import MelaToastContainer from './components/MelaToastContainer';
import MelaRatingPromptContainer from './components/MelaRatingPromptContainer';
import MelaHeldLeadsWatcher from './components/MelaHeldLeadsWatcher';
import webSocketService from './services/webSocketService';
import currencyService from './services/currencyService';
import brandValidationUtils from './utils/brandValidation';
import Sidebar from './components/Layout/LifecycleSidebar';
import NewHeader from './components/Layout/NewHeader';
import CommandPalette from './components/CommandPalette';
import CopilotPanel from './components/CopilotPanel';
import FirstRunTour from './components/FirstRunTour';

// Eager: Login + Landing (first paint)
import Login from './pages/Login';
import LandingPage from './pages/LandingPage';

// Lazy-loaded: All page components (code splitting)
const ActionDashboard = React.lazy(() => import('./pages/ActionDashboard'));
const Autopilot = React.lazy(() => import('./pages/Autopilot'));
const BrandApprovals = React.lazy(() => import('./pages/BrandApprovals'));
const Dashboard = React.lazy(() => import('./pages/Dashboard'));
const CampaignWizard = React.lazy(() => import('./pages/CampaignWizard'));
const SupportCenter = React.lazy(() => import('./pages/SupportCenter'));
const UnifiedAnalytics = React.lazy(() => import('./pages/UnifiedAnalytics'));
const WhatsAppHub = React.lazy(() => import('./pages/WhatsAppHub'));
const AutomationsHub = React.lazy(() => import('./pages/AutomationsHub'));
const NewOnboarding = React.lazy(() => import('./pages/NewOnboarding'));
const Chat = React.lazy(() => import('./pages/Chat'));
const Analytics = React.lazy(() => import('./pages/Analytics'));
const BrandCopilot = React.lazy(() => import('./pages/BrandCopilot'));
const ShopperConcierge = React.lazy(() => import('./pages/ShopperConcierge'));
const JourneyTracking = React.lazy(() => import('./pages/JourneyTracking'));
const WhatsAppIntegration = React.lazy(() => import('./pages/WhatsAppIntegration'));
const WhatsAppTemplateManager = React.lazy(() => import('./pages/WhatsAppTemplateManager'));
const SharedNumberManagement = React.lazy(() => import('./pages/SharedNumberManagement'));
const WhatsAppServiceSetup = React.lazy(() => import('./pages/WhatsAppServiceSetup'));
const WhatsAppSetup = React.lazy(() => import('./pages/WhatsAppSetup'));
const WhatsAppOnboarding = React.lazy(() => import('./pages/WhatsAppOnboarding'));
const WhatsAppCallback = React.lazy(() => import('./pages/WhatsAppCallback'));
const WhatsAppDashboard = React.lazy(() => import('./pages/WhatsAppDashboardNew'));
const MetaWhatsAppSetup = React.lazy(() => import('./pages/MetaWhatsAppSetup'));
const WhatsAppTemplateApproval = React.lazy(() => import('./pages/WhatsAppTemplateApproval'));
const WhatsAppChatInterface = React.lazy(() => import('./pages/WhatsAppChatInterface'));
const VectorDatabase = React.lazy(() => import('./pages/VectorDatabase'));
const Settings = React.lazy(() => import('./pages/Settings'));
const Integrations = React.lazy(() => import('./pages/Integrations'));
const IntegrationForms = React.lazy(() => import('./pages/IntegrationForms'));
const WorkflowAutomation = React.lazy(() => import('./pages/WorkflowAutomation'));
const TeamManagementPro = React.lazy(() => import('./pages/TeamManagementPro'));
const APIManagement = React.lazy(() => import('./pages/APIManagement'));
const JourneyBuilder = React.lazy(() => import('./pages/JourneyBuilder'));
const LeadManagement = React.lazy(() => import('./pages/LeadManagement'));
const ProposalAutomation = React.lazy(() => import('./pages/ProposalAutomation'));
const ProposalGenerator = React.lazy(() => import('./pages/ProposalGenerator'));
const TemplateManagement = React.lazy(() => import('./pages/TemplateManagement'));
const CanvaCallback = React.lazy(() => import('./pages/CanvaCallback'));
const DebugEnv = React.lazy(() => import('./pages/DebugEnv'));
const InventoryManagement = React.lazy(() => import('./pages/InventoryManagement'));
const CustomerSupportQueries = React.lazy(() => import('./pages/CustomerSupportQueries'));
const AccountManagement = React.lazy(() => import('./pages/AccountManagement'));
const RevenueOptimizer = React.lazy(() => import('./pages/RevenueOptimizer'));
const BusinessIntelligence = React.lazy(() => import('./pages/BusinessIntelligence'));
const SystemTest = React.lazy(() => import('./pages/SystemTest'));
const AdvancedInsights = React.lazy(() => import('./pages/AdvancedInsights'));
const JourneyOrchestration = React.lazy(() => import('./pages/JourneyOrchestration'));
const SuperAdminUsageLimits = React.lazy(() => import('./pages/SuperAdminUsageLimits'));
const Onboarding = React.lazy(() => import('./pages/Onboarding'));
const SignUp = React.lazy(() => import('./pages/SignUp'));
const Welcome = React.lazy(() => import('./pages/Welcome'));
const FAQ = React.lazy(() => import('./pages/FAQ'));
const AboutUs = React.lazy(() => import('./pages/AboutUs'));
const CaseStudies = React.lazy(() => import('./pages/CaseStudies'));
const PrivacyPolicy = React.lazy(() => import('./pages/PrivacyPolicy'));
const TermsOfService = React.lazy(() => import('./pages/TermsOfService'));
const Careers = React.lazy(() => import('./pages/Careers'));
const BrandCopilotAI = React.lazy(() => import('./pages/BrandCopilotAI'));
const DocumentIntelligence = React.lazy(() => import('./pages/DocumentIntelligence'));
const AdvancedAnalytics = React.lazy(() => import('./pages/AdvancedAnalytics'));
const ShopperConciergeSolution = React.lazy(() => import('./pages/solutions/ShopperConciergeSolution'));
const WorkflowAutomationSolution = React.lazy(() => import('./pages/solutions/WorkflowAutomationSolution'));
const AnalyticsSolution = React.lazy(() => import('./pages/solutions/AnalyticsSolution'));
const TeamManagementSolution = React.lazy(() => import('./pages/solutions/TeamManagementSolution'));
const IntegrationsSolution = React.lazy(() => import('./pages/solutions/IntegrationsSolution'));
const FlowBuilder = React.lazy(() => import('./pages/FlowBuilder'));
const FlowsList = React.lazy(() => import('./pages/FlowsList'));
const Agents = React.lazy(() => import('./pages/Agents'));
const Reports = React.lazy(() => import('./pages/Reports'));
const ShopifyIntegration = React.lazy(() => import('./pages/ShopifyIntegration'));
const WixIntegration = React.lazy(() => import('./pages/WixIntegration'));
const EmailIntegration = React.lazy(() => import('./pages/EmailIntegration'));
const ClientOnboarding = React.lazy(() => import('./pages/ClientOnboarding'));
const ZeroIntegrationOnboarding = React.lazy(() => import('./pages/ZeroIntegrationOnboarding'));
const Campaigns = React.lazy(() => import('./pages/Campaigns'));
const CartRecovery = React.lazy(() => import('./pages/CartRecovery'));
const SplatLanding = React.lazy(() => import('./pages/SplatLanding'));
const SplatDashboard = React.lazy(() => import('./pages/SplatDashboard'));
const GlitchOnboarding = React.lazy(() => import('./pages/GlitchOnboarding'));
const GlitchRecordingLink = React.lazy(() => import('./pages/GlitchRecordingLink'));
const GlitchRoadmap = React.lazy(() => import('./pages/GlitchRoadmap'));
const GlitchPublicStatus = React.lazy(() => import('./pages/GlitchPublicStatus'));
const GlitchExtension = React.lazy(() => import('./pages/GlitchExtension'));
const RadarExtension = React.lazy(() => import('./pages/RadarExtension'));
const ChutneyPlayground = React.lazy(() => import('./pages/ChutneyPlayground'));
const PlatformStatus = React.lazy(() => import('./pages/PlatformStatus'));
const PulseLanding = React.lazy(() => import('./pages/PulseLanding'));
const PulseDashboard = React.lazy(() => import('./pages/PulseDashboard'));
const MasalaLanding = React.lazy(() => import('./pages/MasalaLanding'));
const TandooriLanding = React.lazy(() => import('./pages/TandooriLanding'));
const BankCaseLanding = React.lazy(() => import('./pages/BankCaseLanding'));
const NoticeAnalyzer = React.lazy(() => import('./pages/NoticeAnalyzer'));
const PublicEventPage = React.lazy(() => import('./pages/PublicEventPage'));
const Pricing = React.lazy(() => import('./pages/Pricing'));
const ComparePage = React.lazy(() => import('./pages/ComparePage'));
const MelaLanding = React.lazy(() => import('./pages/MelaLanding'));
const MelaPrivacyPolicy = React.lazy(() => import('./pages/MelaPrivacyPolicy'));
const MelaDeleteAccount = React.lazy(() => import('./pages/MelaDeleteAccount'));
const MelaDownload = React.lazy(() => import('./pages/MelaDownload'));
const AdminMela = React.lazy(() => import('./pages/AdminMela'));
const AdminMelaPromotions = React.lazy(() => import('./pages/AdminMelaPromotions'));
const AdminMelaTierRequests = React.lazy(() => import('./pages/AdminMelaTierRequests'));
const AdminMelaSponsorRequests = React.lazy(() => import('./pages/AdminMelaSponsorRequests'));
const MelaScanLanding = React.lazy(() => import('./pages/MelaScanLanding'));
const MelaDashboard = React.lazy(() => import('./pages/MelaDashboard'));
const MelaHome = React.lazy(() => import('./pages/MelaHome'));
const MelaYourMela = React.lazy(() => import('./pages/MelaYourMela'));
const MelaDiscovered = React.lazy(() => import('./pages/MelaDiscovered'));
const MelaExhibitions = React.lazy(() => import('./pages/MelaExhibitions'));
const MelaExhibitionDetail = React.lazy(() => import('./pages/MelaExhibitionDetail'));
const MelaAuth = React.lazy(() => import('./pages/MelaAuth'));
const MelaChat = React.lazy(() => import('./pages/MelaChat'));
// chat-v2 — WhatsApp-style group chat (multiple decision-makers)
const MelaCreateGroup = React.lazy(() => import('./pages/MelaCreateGroup'));
const MelaGroupChat = React.lazy(() => import('./pages/MelaGroupChat'));
const MelaCreateExhibition = React.lazy(() => import('./pages/MelaCreateExhibition'));
const MelaVenueManager = React.lazy(() => import('./pages/MelaVenueManager'));
const MelaROICalculator = React.lazy(() => import('./pages/MelaROICalculator'));
const MelaQRScanner = React.lazy(() => import('./pages/MelaQRScanner'));
const MelaServiceQuotes = React.lazy(() => import('./pages/MelaServiceQuotes'));
const MelaCompare = React.lazy(() => import('./pages/MelaCompare'));
const MelaHeatmap = React.lazy(() => import('./pages/MelaHeatmap'));
const MelaFloorPlan = React.lazy(() => import('./pages/MelaFloorPlan'));
const MelaSharedStalls = React.lazy(() => import('./pages/MelaSharedStalls'));
const MelaCommunity = React.lazy(() => import('./pages/MelaCommunity'));
const MelaBuyLeads = React.lazy(() => import('./pages/MelaBuyLeads'));
const MelaFinancing = React.lazy(() => import('./pages/MelaFinancing'));
const MelaDiscover = React.lazy(() => import('./pages/MelaDiscover'));
const MelaForVendors = React.lazy(() => import('./pages/MelaForVendors'));
const MelaPricing = React.lazy(() => import('./pages/MelaPricing'));
const MelaClaimProfile = React.lazy(() => import('./pages/MelaClaimProfile'));
// Mela v2 — Agent batch (pages built by all agents)
const MelaProfile = React.lazy(() => import('./pages/MelaProfile'));
const MelaJoinLanding = React.lazy(() => import('./pages/MelaJoinLanding'));
const MelaOnboarding = React.lazy(() => import('./pages/MelaOnboarding'));
const MelaNotifications = React.lazy(() => import('./pages/MelaNotifications'));
const MelaSaved = React.lazy(() => import('./pages/MelaSaved'));
const MelaBrandCatalog = React.lazy(() => import('./pages/MelaBrandCatalog'));
const MelaProducts = React.lazy(() => import('./pages/MelaProducts'));
const MelaProductDetail = React.lazy(() => import('./pages/MelaProductDetail'));
const MelaCompareSellers = React.lazy(() => import('./pages/MelaCompareSellers'));
const MelaLeadDashboard = React.lazy(() => import('./pages/MelaLeadDashboard'));
const MelaSellerLeads = React.lazy(() => import('./pages/MelaSellerLeads'));
const MelaSellerInbox = React.lazy(() => import('./pages/MelaSellerInbox'));
// IndiaMart-parity: multi-user team accounts (web)
const MelaSellerTeam = React.lazy(() => import('./pages/MelaSellerTeam'));
const MelaTeamAccept = React.lazy(() => import('./pages/MelaTeamAccept'));
const MelaBuyerDashboard = React.lazy(() => import('./pages/MelaBuyerDashboard'));
const MelaSellerTrust = React.lazy(() => import('./pages/MelaSellerTrust'));
const MelaSellerOnboarding = React.lazy(() => import('./pages/MelaSellerOnboarding'));
// Wave 5 — IndiaMart-parity catalog + analytics + promotions (A8-A15)
const MelaSellerWave5 = React.lazy(() => import('./pages/MelaSellerWave5'));
// Wave 6 — IndiaMart-parity buyer search + discovery (C6/C8/C11/C12/C13/C14/C15)
const MelaWishlist = React.lazy(() => import('./pages/MelaWishlist'));
const MelaCompareProducts = React.lazy(() => import('./pages/MelaCompareProducts'));
const MelaBuyerGuides = React.lazy(() => import('./pages/MelaBuyerGuides').then(m => ({ default: m.MelaBuyerGuides })));
const MelaBuyerGuide = React.lazy(() => import('./pages/MelaBuyerGuides').then(m => ({ default: m.MelaBuyerGuide })));
const MelaPublicBrandProfile = React.lazy(() => import('./pages/MelaPublicBrandProfile'));
const MelaSellerAutoResponder = React.lazy(() => import('./pages/MelaSellerAutoResponder'));
const MelaSellerCatalogImport = React.lazy(() => import('./pages/MelaSellerCatalogImport'));
const MelaSellerFeatured = React.lazy(() => import('./pages/MelaSellerFeatured'));
const MelaSellerCompetitors = React.lazy(() => import('./pages/MelaSellerCompetitors'));
const MelaSellerPipeline = React.lazy(() => import('./pages/MelaSellerPipeline'));
const MelaVendorCalendar = React.lazy(() => import('./pages/MelaVendorCalendar'));
const MelaMyStallQR = React.lazy(() => import('./pages/MelaMyStallQR'));
const MelaBrandsList = React.lazy(() => import('./pages/MelaBrandsList'));
const MelaExplore = React.lazy(() => import('./pages/MelaExplore'));
const MelaBrandCard = React.lazy(() => import('./pages/MelaBrandCard'));
const MelaCategories = React.lazy(() => import('./pages/MelaCategories'));
const MelaEnquiryStatus = React.lazy(() => import('./pages/MelaEnquiryStatus'));
const MelaQuoteBuilder = React.lazy(() => import('./pages/MelaQuoteBuilder'));
const MelaQuotePrint = React.lazy(() => import('./pages/MelaQuotePrint'));
const MelaRFQWizard = React.lazy(() => import('./pages/MelaRFQWizard'));
const MelaBrandFollow = React.lazy(() => import('./pages/MelaBrandFollow'));
const MelaBrandInviteDetail = React.lazy(() => import('./pages/MelaBrandInviteDetail'));
const MelaVendorPassport = React.lazy(() => import('./pages/MelaVendorPassport'));
const MelaOrganiserHome = React.lazy(() => import('./pages/MelaOrganiserHome'));
const MelaScoutBrand = React.lazy(() => import('./pages/MelaScoutBrand'));
const MelaEditExhibition = React.lazy(() => import('./pages/MelaEditExhibition'));
const MelaEventRSVP = React.lazy(() => import('./pages/MelaEventRSVP'));
const MelaCheckin = React.lazy(() => import('./pages/MelaCheckin'));
const MelaVendorRequests = React.lazy(() => import('./pages/MelaVendorRequests'));
const MelaOrganiserAnalytics = React.lazy(() => import('./pages/MelaOrganiserAnalytics'));
const MelaInviteToEvent = React.lazy(() => import('./pages/MelaInviteToEvent'));
const MelaJobBoard = React.lazy(() => import('./pages/MelaJobBoard'));
const MelaPitchesReceived = React.lazy(() => import('./pages/MelaPitchesReceived'));
const MelaSuggestedSponsors = React.lazy(() => import('./pages/MelaSuggestedSponsors'));
const MelaStaffCodes = React.lazy(() => import('./pages/MelaStaffCodes'));
const MelaVenueDiscover = React.lazy(() => import('./pages/MelaVenueDiscover'));
const MelaVenueBookings = React.lazy(() => import('./pages/MelaVenueBookings'));
const MelaVenuePnL = React.lazy(() => import('./pages/MelaVenuePnL'));
const MelaVenueEventRequests = React.lazy(() => import('./pages/MelaVenueEventRequests'));
const MelaVenueSearch = React.lazy(() => import('./pages/MelaVenueSearch'));
const MelaVenuePricingRules = React.lazy(() => import('./pages/MelaVenuePricingRules'));
const MelaExhibitionRoom = React.lazy(() => import('./pages/MelaExhibitionRoom'));
const MelaStallMap = React.lazy(() => import('./pages/MelaStallMap'));
const MelaBrowseOrganisers = React.lazy(() => import('./pages/MelaBrowseOrganisers'));
const MelaBrowseProviders = React.lazy(() => import('./pages/MelaBrowseProviders'));
const MelaServiceSearch = React.lazy(() => import('./pages/MelaServiceSearch'));
const MelaServicePortfolio = React.lazy(() => import('./pages/MelaServicePortfolio'));
const MelaProviderPortfolio = React.lazy(() => import('./pages/MelaProviderPortfolio'));
const MelaMyQuoteRequests = React.lazy(() => import('./pages/MelaMyQuoteRequests'));
const MelaSponsor = React.lazy(() => import('./pages/MelaSponsor'));
const MelaBrandDiscover = React.lazy(() => import('./pages/MelaBrandDiscover'));
const MelaPublicProfile = React.lazy(() => import('./pages/MelaPublicProfile'));
const MelaInviteContacts = React.lazy(() => import('./pages/MelaInviteContacts'));
const MelaReviewSubmit = React.lazy(() => import('./pages/MelaReviewSubmit'));
const MelaPrepChecklist = React.lazy(() => import('./pages/MelaPrepChecklist'));
const MelaPostVisitRecap = React.lazy(() => import('./pages/MelaPostVisitRecap'));
const MelaComplaint = React.lazy(() => import('./pages/MelaComplaint'));
const MelaBeginnerGuide = React.lazy(() => import('./pages/MelaBeginnerGuide'));
const MelaHelp = React.lazy(() => import('./pages/MelaHelp'));
const MelaFriendsActivity = React.lazy(() => import('./pages/MelaFriendsActivity'));
const MelaGroupPlan = React.lazy(() => import('./pages/MelaGroupPlan'));
const MelaExhibitionGroupChat = React.lazy(() => import('./pages/MelaExhibitionGroupChat'));
const MelaBilling = React.lazy(() => import('./pages/MelaBilling'));
const MelaSubscription = React.lazy(() => import('./pages/MelaSubscription'));
// IndiaMart-parity: invoices
const MelaInvoices = React.lazy(() => import('./pages/MelaInvoices'));
const MelaPremium = React.lazy(() => import('./pages/MelaPremium'));
const MelaPromote = React.lazy(() => import('./pages/MelaPromote'));
const MelaOrders = React.lazy(() => import('./pages/MelaOrders'));
const MelaAchievements = React.lazy(() => import('./pages/MelaAchievements'));
const MelaReferral = React.lazy(() => import('./pages/MelaReferral'));
const MelaVisitorPassport = React.lazy(() => import('./pages/MelaVisitorPassport'));
const MelaCrowdPrediction = React.lazy(() => import('./pages/MelaCrowdPrediction'));
const MelaLiveStories = React.lazy(() => import('./pages/MelaLiveStories'));
const MelaExhibitionHaul = React.lazy(() => import('./pages/MelaExhibitionHaul'));
// Mela v2 — This agent's 9 pages
const MelaTerms = React.lazy(() => import('./pages/MelaTerms'));
const MelaGiftCards = React.lazy(() => import('./pages/MelaGiftCards'));
const MelaVirtualExhibition = React.lazy(() => import('./pages/MelaVirtualExhibition'));
const MelaBrandContest = React.lazy(() => import('./pages/MelaBrandContest'));
const MelaPhotoBooth = React.lazy(() => import('./pages/MelaPhotoBooth'));
const MelaInfluencer = React.lazy(() => import('./pages/MelaInfluencer'));
const MelaCompliance = React.lazy(() => import('./pages/MelaCompliance'));
const MelaPress = React.lazy(() => import('./pages/MelaPress'));
const MelaAdminFunnel = React.lazy(() => import('./pages/MelaAdminFunnel'));
const MelaDirectory = React.lazy(() => import('./pages/MelaDirectory'));
const MelaDirectoryInquiries = React.lazy(() => import('./pages/MelaDirectoryInquiries'));
const MelaVenueCalendar = React.lazy(() => import('./pages/MelaVenueCalendar'));
const MelaDailyScreen = React.lazy(() => import('./pages/MelaDailyScreen'));
const PartnersDashboard = React.lazy(() => import('./pages/PartnersDashboard'));
// SerialBatchTracker removed
// CashTracking removed — payments captured in invoicing
const CouponManager = React.lazy(() => import('./pages/CouponManager'));
const ReturnsManager = React.lazy(() => import('./pages/ReturnsManager'));
const ShippingTracker = React.lazy(() => import('./pages/ShippingTracker'));
const PendingActivation = React.lazy(() => import('./pages/PendingActivation'));
// UserActivation removed — handled by SuperAdmin portal
const SuperAdminLogin = React.lazy(() => import('./pages/SuperAdminLogin'));
const SuperAdminDashboard = React.lazy(() => import('./pages/SuperAdminDashboard'));
const UsageDashboard = React.lazy(() => import('./pages/UsageDashboard'));
const CustomerCRM = React.lazy(() => import('./pages/CustomerCRM'));
const CustomerIntelligence = React.lazy(() => import('./pages/CustomerIntelligence'));
const Governance = React.lazy(() => import('./pages/Governance'));
const DataSources = React.lazy(() => import('./pages/DataSources'));
const AskAICOS = React.lazy(() => import('./pages/AskAICOS'));
const SegmentBuilder = React.lazy(() => import('./pages/SegmentBuilder'));
const SocialCommerce = React.lazy(() => import('./pages/SocialCommerce'));
// ABTesting removed
const LoyaltyProgram = React.lazy(() => import('./pages/LoyaltyProgram'));
const OrderManagement = React.lazy(() => import('./pages/OrderManagement'));
const ProductCatalog = React.lazy(() => import('./pages/ProductCatalog'));
const ReviewsDashboard = React.lazy(() => import('./pages/ReviewsDashboard'));
const NotificationPreferences = React.lazy(() => import('./pages/NotificationPreferences'));
const StoreManagement = React.lazy(() => import('./pages/StoreManagement'));
const PurchaseVendor = React.lazy(() => import('./pages/PurchaseVendor'));
const NotFound = React.lazy(() => import('./pages/NotFound'));
const ForgotPassword = React.lazy(() => import('./pages/ForgotPassword'));
const ResetPasswordPage = React.lazy(() => import('./pages/ResetPassword'));

// Growth & Viral Tools (public, no auth)
const CACCalculator = React.lazy(() => import('./pages/CACCalculator'));
const WhatsAppROICalculator = React.lazy(() => import('./pages/WhatsAppROICalculator'));
const CODCalculator = React.lazy(() => import('./pages/CODCalculator'));
const RetentionCalculator = React.lazy(() => import('./pages/RetentionCalculator'));
const WhatsAppTemplateGallery = React.lazy(() => import('./pages/WhatsAppTemplateGallery'));
const ShopifyCartRecovery = React.lazy(() => import('./pages/ShopifyCartRecovery'));
const D2CBenchmarks = React.lazy(() => import('./pages/D2CBenchmarks'));

// IndiaMart-parity wave 4
const MelaSellerWebhooks = React.lazy(() => import('./pages/MelaSellerWebhooks'));
const MelaSellerAnalytics = React.lazy(() => import('./pages/MelaSellerAnalytics'));

// Suspense fallback for lazy components
function PageLoader() {
  return (
    <div className="flex items-center justify-center min-h-[60vh]">
      <div className="flex flex-col items-center space-y-3">
        <div className="w-8 h-8 border-2 border-violet-600 border-t-transparent rounded-full animate-spin" />
        <p className="text-sm text-gray-500">Loading...</p>
      </div>
    </div>
  );
}

const queryClient = new QueryClient();

function AppLayout({ children }: { children: React.ReactNode }) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [commandPaletteOpen, setCommandPaletteOpen] = useState(false);
  const [copilotOpen, setCopilotOpen] = useState(false);
  const [showTour, setShowTour] = useState(() => {
    const shouldShow = localStorage.getItem('show_tour') === 'true' && !localStorage.getItem('tour_completed');
    return shouldShow;
  });

  // Global keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Ctrl+K or Cmd+K — Command Palette
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        setCommandPaletteOpen(prev => !prev);
      }
      // Ctrl+/ — Copilot Panel
      if ((e.ctrlKey || e.metaKey) && e.key === '/') {
        e.preventDefault();
        setCopilotOpen(prev => !prev);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  return (
    <div className="app-layout">
      <Sidebar sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />
      <div className="content-area">
        <NewHeader
          setSidebarOpen={setSidebarOpen}
          onOpenCommandPalette={() => setCommandPaletteOpen(true)}
          onOpenCopilot={() => setCopilotOpen(prev => !prev)}
        />
        <main id="main-content" className="main-scroll" role="main">
          <div className="main-content">
            {children}
          </div>
        </main>
      </div>
      <CommandPalette isOpen={commandPaletteOpen} onClose={() => setCommandPaletteOpen(false)} />
      <CopilotPanel isOpen={copilotOpen} onClose={() => setCopilotOpen(false)} />
      {showTour && <FirstRunTour isOpen={showTour} onComplete={() => { setShowTour(false); localStorage.removeItem('show_tour'); localStorage.setItem('tour_completed', 'true'); }} />}
    </div>
  );
}

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, loading, user } = useAuth();

  // Auth bypass removed — production ready

  // Initialize WebSocket when user is authenticated
  useEffect(() => {
    if (isAuthenticated && user) {
      const initializeWebSocket = async () => {
        try {
          const brandId = localStorage.getItem('brand_id') || user.brandId || '';
          if (!brandId) return; // No brand — skip WebSocket
          const token = localStorage.getItem('auth_token') || '';

          console.log('🔌 Initializing WebSocket connection with brand:', brandId);
          await webSocketService.autoConnect(brandId);
          
          // Set up global message handlers
          webSocketService.on('connection_established', (message) => {
            console.log('🎉 WebSocket connection established:', message);
          });

          webSocketService.on('analytics_update', (message) => {
            console.log('📊 Real-time analytics update received:', message);
          });

          webSocketService.on('new_chat_message', (message) => {
            console.log('💬 New chat message received:', message);
          });

          webSocketService.on('document_processed', (message) => {
            console.log('📄 Document processing completed:', message);
          });

          webSocketService.on('proposal_automation_progress', (message) => {
            console.log('🤖 Proposal automation progress:', message);
          });

          webSocketService.on('*', (message) => {
            // Global handler for all messages
            console.log('📨 Global WebSocket message:', message.type);
          });

        } catch (error) {
          console.error('❌ Failed to initialize WebSocket:', error);
        }
      };

      initializeWebSocket();

      // Cleanup on unmount or auth change
      return () => {
        try {
          webSocketService.disconnect();
        } catch (error) {
          console.error('Error disconnecting WebSocket:', error);
        }
      };
    }
  }, [isAuthenticated, user]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // First-login detection: redirect to onboarding if never completed
  const onboardingDone = localStorage.getItem('onboarding_completed');
  if (!onboardingDone && window.location.pathname !== '/new-onboarding') {
    return <Navigate to="/new-onboarding" replace />;
  }

  return <AppLayout>{children}</AppLayout>;
}

// On melamarketplace.app, Mela routes are mounted at root (no `/mela` prefix)
// so URLs are clean — `melamarketplace.app/exhibitions`, not `.../mela/exhibitions`.
// `m()` strips the legacy `/mela` prefix from a route's path when we're on the
// Mela-only domain; on idliapam.in it returns the path unchanged.
const IS_MELA_DOMAIN =
  typeof window !== 'undefined' &&
  (window.location.hostname === 'melamarketplace.app' ||
    window.location.hostname === 'www.melamarketplace.app');

function m(path: string): string {
  if (!IS_MELA_DOMAIN) return path;
  if (path === '/mela') return '/';
  if (path.startsWith('/mela/')) return path.slice(5);
  return path;
}

// Client-side fallback for internal nav calls that still use hardcoded
// `/mela/...` paths (50+ call sites across the Mela frontend). When a
// component does `nav('/mela/auth')` on melamarketplace.app, React Router
// pushes the URL but no route matches (the routes are mounted at root via
// `m()`). This component catches `/mela/*` on the Mela domain and redirects
// to the unprefixed URL, preserving query string + hash.
//
// External / initial loads are handled separately by a Vercel 301 redirect
// (faster + better for SEO crawl budget).
function StripMelaPrefix() {
  const location = useLocation();
  const stripped = location.pathname.replace(/^\/mela/, '') || '/';
  return <Navigate to={stripped + location.search + location.hash} replace />;
}

function App() {
  useEffect(() => {
    // Initialize services when app starts
    const initializeServices = async () => {
      try {
        console.log('🚀 Initializing AI Commerce OS services...');
        
        // Initialize brand validation first (critical for API calls)
        console.log('🔧 Initializing brand validation...');
        brandValidationUtils.initializeBrandValidation();
        
        // Initialize currency service
        console.log('💱 Initializing currency service...');
        await currencyService.getCurrentRate();
        currencyService.startAutoUpdate(30); // Update every 30 minutes
        
        console.log('✅ Services initialized successfully');

        // Dogfood widgets (Glitch + Radar) were auto-injected here for
        // 'we're the first customer of our own products' demos. Two
        // problems in production:
        //   1. radar-widget.js top-level-declares `const DEFAULT_ENDPOINT`,
        //      so a second load throws a SyntaxError.
        //   2. The widget POLLs pulse/widget/announcements which is rate
        //      limited and returns 400 on OPTIONS, filling the console.
        // They add no value inside the admin dashboard itself — removed.
        // If we want to dogfood them on a real user-facing surface, load
        // them from that surface, not here.

      } catch (error) {
        console.error('❌ Failed to initialize services:', error);
        // Don't throw the error, just log it
      }
    };

    initializeServices();

    // Cleanup on unmount
    return () => {
      try {
        currencyService.stopAutoUpdate();
      } catch (error) {
        console.error('Error cleaning up services:', error);
      }
    };
  }, []);

  return (
    <HelmetProvider>
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <BrandProvider>
            <Router>
              <MelaAppBanner />
              <MelaBridgeIndicator />
              <MelaToastContainer />
              <MelaRatingPromptContainer />
              <MelaHeldLeadsWatcher />
              <div className="App min-h-screen">
                <Suspense fallback={<PageLoader />}>
                <Routes>
              {/* Public Routes — on melamarketplace.app, `/` serves the Mela landing instead of AICOS */}
              <Route path="/" element={IS_MELA_DOMAIN ? <MelaLanding /> : <LandingPage />} />
              <Route path="/home" element={<Navigate to="/dashboard" replace />} />
              <Route path="/about" element={<AboutUs />} />
              <Route path="/case-studies" element={<CaseStudies />} />
              <Route path="/careers" element={<Careers />} />
              <Route path="/privacy" element={<PrivacyPolicy />} />
              <Route path="/terms" element={<TermsOfService />} />
              {/* Public signup enabled */}
              <Route path="/signup" element={<Navigate to="/login?signup=true" replace />} />
              <Route path="/welcome" element={<Welcome />} />
              <Route path="/login" element={<Login />} />
              <Route path="/pending-activation" element={<PendingActivation />} />
              <Route path="/debug-env" element={<DebugEnv />} />
              
              {/* Super Admin Routes - Public Access */}
              <Route path="/superadmin/login" element={<SuperAdminLogin />} />
              <Route path="/superadmin/dashboard" element={<SuperAdminDashboard />} />
              
              {/* Hidden Super Admin Route - Complex URL */}
              <Route path="/sys-admin-portal-2026-secure-access-v2/dashboard" element={<SuperAdminDashboard />} />
              <Route path="/sys-admin-portal-2026-secure-access-v2/login" element={<SuperAdminLogin />} />
              <Route path="/sys-admin-portal-2026-secure-access-v2" element={<SuperAdminLogin />} />
              
              {/* Client Onboarding - Public Routes */}
              <Route path="/zero-onboarding" element={<ZeroIntegrationOnboarding />} />
              <Route path="/client-onboarding" element={<ClientOnboarding />} />

              {/* Splat Bug Reporter */}
              <Route path="/glitch" element={<SplatLanding />} />
              <Route path="/glitch/signup" element={<GlitchOnboarding />} />
              <Route path="/glitch/record/:linkId" element={<GlitchRecordingLink />} />
              <Route path="/glitch/roadmap/:projectKey" element={<GlitchRoadmap />} />
              <Route path="/glitch/status/:projectKey" element={<GlitchPublicStatus />} />
              <Route path="/glitch/extension" element={<GlitchExtension />} />
              <Route path="/glitch/dashboard" element={<SplatDashboard />} />

              {/* Chutney API Playground */}
              <Route path="/chutney/playground" element={<ChutneyPlayground />} />

              {/* Platform Status */}
              <Route path="/status" element={<PlatformStatus />} />

              {/* Radar Announcements */}
              <Route path="/radar" element={<PulseLanding />} />
              <Route path="/masala" element={<MasalaLanding />} />
              <Route path="/legalarm" element={<TandooriLanding />} />
              <Route path="/bank-case" element={<BankCaseLanding />} />
              <Route path="/notice-analyzer" element={<NoticeAnalyzer />} />
              <Route path="/event/:slug" element={<PublicEventPage />} />
              <Route path="/radar/dashboard" element={<PulseDashboard />} />
              <Route path="/radar/extension" element={<RadarExtension />} />

              {/* Mela — Exhibition Marketplace */}
              {/* On melamarketplace.app, internal nav('/mela/X') calls land here
                  and get rewritten to /X so the cleanly-mounted Mela routes match.
                  External hits to /mela/X are intercepted by Vercel 301s first. */}
              {IS_MELA_DOMAIN && <Route path="/mela/*" element={<StripMelaPrefix />} />}
              <Route path={m("/mela")} element={<MelaLanding />} />
              <Route path={m("/mela/privacy")} element={<MelaPrivacyPolicy />} />
              <Route path={m("/mela/delete-account")} element={<MelaDeleteAccount />} />
              <Route path={m("/mela/download")} element={<MelaDownload />} />
              <Route path="/admin/mela" element={<AdminMela />} />
              <Route path="/admin/mela/promotions" element={<AdminMelaPromotions />} />
              <Route path="/admin/mela/tier-requests" element={<AdminMelaTierRequests />} />
              <Route path="/admin/mela/sponsor-requests" element={<AdminMelaSponsorRequests />} />
              <Route path={m("/mela/exhibitions")} element={<MelaExhibitions />} />
              <Route path={m("/mela/exhibition/:slug")} element={<MelaExhibitionDetail />} />
              <Route path={m("/mela/dashboard")} element={<MelaDashboard />} />
              <Route path={m("/mela/home")} element={<MelaHome />} />
              <Route path={m("/mela/your-mela")} element={<MelaYourMela />} />
              <Route path={m("/mela/discovered")} element={<MelaDiscovered />} />
              <Route path={m("/mela/auth")} element={<MelaAuth />} />
              <Route path={m("/mela/chat")} element={<MelaChat />} />
              {/* chat-v2 — group chat (WhatsApp-style multi-party) */}
              <Route path={m("/mela/group/new")} element={<MelaCreateGroup />} />
              <Route path={m("/mela/group/:id")} element={<MelaGroupChat />} />
              <Route path={m("/mela/exhibitions/create")} element={<MelaCreateExhibition />} />
              <Route path={m("/mela/create-exhibition")} element={<MelaCreateExhibition />} />
              <Route path={m("/mela/venues/manage")} element={<MelaVenueManager />} />
              <Route path={m("/mela/roi")} element={<MelaROICalculator />} />
              <Route path={m("/mela/scan/custom/:vendorId")} element={<MelaScanLanding />} />
              <Route path={m("/mela/scan/:exhibitionId/:vendorId")} element={<MelaScanLanding />} />
              <Route path={m("/mela/scan")} element={<MelaQRScanner />} />
              <Route path={m("/mela/quotes")} element={<MelaServiceQuotes />} />
              <Route path={m("/mela/venues")} element={<MelaVenueManager />} />
              <Route path={m("/mela/compare")} element={<MelaCompare />} />
              <Route path={m("/mela/heatmap")} element={<MelaHeatmap />} />
              <Route path={m("/mela/floor-plan")} element={<MelaFloorPlan />} />
              <Route path={m("/mela/shared-stalls")} element={<MelaSharedStalls />} />
              <Route path={m("/mela/community")} element={<MelaCommunity />} />
              <Route path={m("/mela/buy-leads")} element={<MelaBuyLeads />} />
              <Route path={m("/mela/financing")} element={<MelaFinancing />} />
              <Route path={m("/mela/discover")} element={<MelaDiscover />} />

              {/* Mela v2 — All agent routes */}
              <Route path={m("/mela/join/:token")} element={<MelaJoinLanding />} />
              <Route path="/join/:token" element={<MelaJoinLanding />} />
              <Route path={m("/mela/profile")} element={<MelaProfile />} />
              <Route path={m("/mela/onboarding")} element={<MelaOnboarding />} />
              <Route path={m("/mela/notifications")} element={<MelaNotifications />} />
              <Route path={m("/mela/saved")} element={<MelaSaved />} />
              <Route path={m("/mela/catalog")} element={<MelaBrandCatalog />} />
              {/* Catalog upgrade — bulk-price product discovery, detail w/ gallery+specs, compare sellers */}
              <Route path={m("/mela/products")} element={<MelaProducts />} />
              <Route path={m("/mela/p/:slugOrId")} element={<MelaProductDetail />} />
              <Route path={m("/mela/compare-sellers/:slug")} element={<MelaCompareSellers />} />
              <Route path={m("/mela/leads")} element={<MelaLeadDashboard />} />
              <Route path={m("/mela/seller/leads")} element={<MelaSellerLeads />} />
              <Route path={m("/mela/seller/inbox")} element={<MelaSellerInbox />} />
              <Route path={m("/mela/seller/trust")} element={<MelaSellerTrust />} />
              <Route path={m("/mela/seller/onboarding")} element={<MelaSellerOnboarding />} />
              <Route path={m("/mela/seller/wave5")} element={<MelaSellerWave5 />} />
              {/* Wave 6 — buyer search + discovery */}
              <Route path={m("/mela/wishlist")} element={<MelaWishlist />} />
              <Route path={m("/mela/compare-products")} element={<MelaCompareProducts />} />
              <Route path={m("/mela/guides")} element={<MelaBuyerGuides />} />
              <Route path={m("/mela/guides/:slug")} element={<MelaBuyerGuide />} />
              <Route path={m("/mela/buyer")} element={<MelaBuyerDashboard />} />
              <Route path="/b/:slug" element={<MelaPublicBrandProfile />} />
              <Route path={m("/mela/seller/auto-responder")} element={<MelaSellerAutoResponder />} />
              <Route path={m("/mela/seller/catalog-import")} element={<MelaSellerCatalogImport />} />
              <Route path={m("/mela/seller/featured")} element={<MelaSellerFeatured />} />
              <Route path={m("/mela/seller/competitors")} element={<MelaSellerCompetitors />} />
              <Route path={m("/mela/seller/pipeline")} element={<MelaSellerPipeline />} />
              <Route path={m("/mela/vendor/calendar")} element={<MelaVendorCalendar />} />
              <Route path={m("/mela/stall/qr")} element={<MelaMyStallQR />} />
              <Route path={m("/mela/explore")} element={<MelaExplore />} />
              <Route path={m("/mela/brands")} element={<MelaBrandsList />} />
              <Route path={m("/mela/brand/:id")} element={<MelaBrandCard />} />
              <Route path={m("/mela/categories")} element={<MelaCategories />} />
              <Route path={m("/mela/enquiry/:enquiryId/status")} element={<MelaEnquiryStatus />} />
              <Route path={m("/mela/quote/new")} element={<MelaQuoteBuilder />} />
              <Route path={m("/mela/quote/:id/print")} element={<MelaQuotePrint />} />
              <Route path={m("/mela/rfq/new")} element={<MelaRFQWizard />} />
              <Route path={m("/mela/brands/following")} element={<MelaBrandFollow />} />
              <Route path={m("/mela/invite/:id")} element={<MelaBrandInviteDetail />} />
              <Route path={m("/mela/vendor/passport")} element={<MelaVendorPassport />} />
              <Route path={m("/mela/organiser")} element={<MelaOrganiserHome />} />
              <Route path={m("/mela/visited-brands")} element={<MelaScoutBrand />} />
              <Route path={m("/mela/exhibition/:id/edit")} element={<MelaEditExhibition />} />
              <Route path={m("/mela/rsvp")} element={<MelaEventRSVP />} />
              <Route path={m("/mela/checkin")} element={<MelaCheckin />} />
              <Route path={m("/mela/vendor-requests")} element={<MelaVendorRequests />} />
              <Route path={m("/mela/analytics")} element={<MelaOrganiserAnalytics />} />
              <Route path={m("/mela/invite-to-event")} element={<MelaInviteToEvent />} />
              <Route path={m("/mela/jobs")} element={<MelaJobBoard />} />
              <Route path={m("/mela/pitches")} element={<MelaPitchesReceived />} />
              <Route path={m("/mela/sponsors")} element={<MelaSuggestedSponsors />} />
              <Route path={m("/mela/staff-codes")} element={<MelaStaffCodes />} />
              <Route path={m("/mela/venues/discover")} element={<MelaVenueDiscover />} />
              <Route path={m("/mela/venue/bookings")} element={<MelaVenueBookings />} />
              <Route path={m("/mela/venue/pnl")} element={<MelaVenuePnL />} />
              <Route path={m("/mela/venue/event-requests")} element={<MelaVenueEventRequests />} />
              <Route path={m("/mela/venues/search")} element={<MelaVenueSearch />} />
              <Route path={m("/mela/venue/:venueId/pricing-rules")} element={<MelaVenuePricingRules />} />
              <Route path={m("/mela/exhibition/:id/room")} element={<MelaExhibitionRoom />} />
              <Route path={m("/mela/stall-map")} element={<MelaStallMap />} />
              <Route path={m("/mela/organisers")} element={<MelaBrowseOrganisers />} />
              <Route path={m("/mela/service-providers")} element={<MelaBrowseProviders />} />
              <Route path={m("/mela/services/search")} element={<MelaServiceSearch />} />
              <Route path={m("/mela/service-portfolio")} element={<MelaServicePortfolio />} />
              <Route path={m("/mela/provider-portfolio")} element={<MelaProviderPortfolio />} />
              <Route path={m("/mela/quote-requests")} element={<MelaMyQuoteRequests />} />
              <Route path={m("/mela/sponsor")} element={<MelaSponsor />} />
              <Route path={m("/mela/brands/discover")} element={<MelaBrandDiscover />} />
              <Route path={m("/mela/profile/:userId")} element={<MelaPublicProfile />} />
              <Route path={m("/mela/invite-contacts")} element={<MelaInviteContacts />} />
              <Route path={m("/mela/review/:id")} element={<MelaReviewSubmit />} />
              <Route path={m("/mela/checklist")} element={<MelaPrepChecklist />} />
              <Route path={m("/mela/recap")} element={<MelaPostVisitRecap />} />
              <Route path={m("/mela/complaint")} element={<MelaComplaint />} />
              <Route path={m("/mela/guide")} element={<MelaBeginnerGuide />} />
              <Route path={m("/mela/help")} element={<MelaHelp />} />
              <Route path={m("/mela/friends")} element={<MelaFriendsActivity />} />
              <Route path={m("/mela/group-plan")} element={<MelaGroupPlan />} />
              <Route path={m("/mela/exhibition/:id/group-chat")} element={<MelaExhibitionGroupChat />} />
              <Route path={m("/mela/billing")} element={<MelaBilling />} />
              <Route path={m("/mela/subscription")} element={<MelaSubscription />} />
              <Route path={m("/mela/premium")} element={<MelaPremium />} />
              <Route path={m("/mela/promote")} element={<MelaPromote />} />
              <Route path={m("/mela/orders")} element={<MelaOrders />} />
              <Route path={m("/mela/achievements")} element={<MelaAchievements />} />
              <Route path={m("/mela/referral")} element={<MelaReferral />} />
              <Route path={m("/mela/passport")} element={<MelaVisitorPassport />} />
              <Route path={m("/mela/when-to-visit")} element={<MelaCrowdPrediction />} />
              <Route path={m("/mela/live")} element={<MelaLiveStories />} />
              <Route path={m("/mela/haul")} element={<MelaExhibitionHaul />} />
              <Route path={m("/mela/terms")} element={<MelaTerms />} />
              <Route path={m("/mela/gift-cards")} element={<MelaGiftCards />} />
              <Route path={m("/mela/virtual")} element={<MelaVirtualExhibition />} />
              <Route path={m("/mela/contest")} element={<MelaBrandContest />} />
              <Route path={m("/mela/photo-booth")} element={<MelaPhotoBooth />} />
              <Route path={m("/mela/influencer")} element={<MelaInfluencer />} />
              <Route path={m("/mela/compliance")} element={<MelaCompliance />} />
              <Route path={m("/mela/press")} element={<MelaPress />} />
              <Route path={m("/mela/admin/funnel")} element={<MelaAdminFunnel />} />
              <Route path={m("/mela/directory")} element={<MelaDirectory />} />
                 <Route path={m("/mela/for-vendors")} element={<MelaForVendors />} />
              <Route path={m("/mela/pricing")} element={<MelaPricing />} />
              <Route path={m("/mela/claim/:token")} element={<MelaClaimProfile />} />
              <Route path={m("/mela/for-organisers")} element={<MelaDirectory />} />
              <Route path={m("/mela/admin/directory-contacts")} element={<MelaDirectoryInquiries />} />
              <Route path={m("/mela/venue-calendar")} element={<MelaVenueCalendar />} />
              <Route path={m("/mela/daily")} element={<MelaDailyScreen />} />

              {/* IndiaMart-parity: multi-user team accounts (web) */}
              <Route path={m("/mela/seller/team")} element={<MelaSellerTeam />} />
              <Route path={m("/mela/team/accept/:token")} element={<MelaTeamAccept />} />

              {/* IndiaMart-parity: invoices */}
              <Route path={m("/mela/account/invoices")} element={<MelaInvoices />} />

              {/* Partners Dashboard — cross-product MIS */}
              <Route path="/partners" element={<PartnersDashboard />} />
              <Route path="/admin/partners" element={<PartnersDashboard />} />
              <Route path={m("/mela/services")} element={<MelaServiceQuotes />} />

              {/* Pricing & Comparisons - Public */}
              <Route path="/pricing" element={<Pricing />} />
              <Route path="/compare/:slug" element={<ComparePage />} />

              {/* Solution Pages - Public */}
              <Route path="/brand-copilot-ai" element={<BrandCopilotAI />} />
              <Route path="/document-intelligence" element={<DocumentIntelligence />} />
              <Route path="/advanced-analytics" element={<AdvancedAnalytics />} />
              <Route path="/solutions/shopper-concierge" element={<ShopperConciergeSolution />} />
              <Route path="/solutions/workflow-automation" element={<WorkflowAutomationSolution />} />
              <Route path="/solutions/analytics" element={<AnalyticsSolution />} />
              <Route path="/solutions/team-management" element={<TeamManagementSolution />} />
              <Route path="/solutions/integrations" element={<IntegrationsSolution />} />

              {/* Free Tools — SEO & Viral Growth (public, no auth) */}
              <Route path="/tools/cac-calculator" element={<CACCalculator />} />
              <Route path="/tools/whatsapp-roi" element={<WhatsAppROICalculator />} />
              <Route path="/tools/cod-calculator" element={<CODCalculator />} />
              <Route path="/tools/retention-calculator" element={<RetentionCalculator />} />
              <Route path="/templates/gallery" element={<WhatsAppTemplateGallery />} />
              <Route path="/shopify/cart-recovery" element={<ShopifyCartRecovery />} />
              <Route path="/benchmarks" element={<D2CBenchmarks />} />

              {/* Protected Routes */}
              <Route
                path="/dashboard"
                element={
                  <ProtectedRoute>
                    <ActionDashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dashboard-classic"
                element={
                  <ProtectedRoute>
                    <Dashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/autopilot"
                element={
                  <ProtectedRoute>
                    <Autopilot />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/superadmin/approvals"
                element={
                  <ProtectedRoute>
                    <BrandApprovals />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/usage-dashboard"
                element={
                  <ProtectedRoute>
                    <UsageDashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/superadmin-usage-limits"
                element={
                  <ProtectedRoute>
                    <SuperAdminUsageLimits />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/chat"
                element={
                  <ProtectedRoute>
                    <Chat />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/analytics"
                element={
                  <ProtectedRoute>
                    <Analytics />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/reports"
                element={
                  <ProtectedRoute>
                    <Reports />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/flows"
                element={
                  <ProtectedRoute>
                    <FlowsList />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/flow-builder"
                element={
                  <ProtectedRoute>
                    <FlowBuilder />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/workflow-builder"
                element={
                  <ProtectedRoute>
                    <FlowBuilder />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/brand-copilot"
                element={
                  <ProtectedRoute>
                    <BrandCopilot />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/shopper-concierge"
                element={
                  <ProtectedRoute>
                    <ShopperConcierge />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/journey-tracking"
                element={
                  <ProtectedRoute>
                    <JourneyTracking />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/revenue-optimizer"
                element={
                  <ProtectedRoute>
                    <RevenueOptimizer />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/business-intelligence"
                element={
                  <ProtectedRoute>
                    <BusinessIntelligence />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/whatsapp-templates"
                element={
                  <ProtectedRoute>
                    <WhatsAppTemplateManager />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/shared-whatsapp"
                element={
                  <ProtectedRoute>
                    <SharedNumberManagement />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/whatsapp-onboarding"
                element={
                  <ProtectedRoute>
                    <WhatsAppOnboarding />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/whatsapp-callback"
                element={<WhatsAppCallback />}
              />
              <Route
                path="/whatsapp-setup"
                element={
                  <ProtectedRoute>
                    <WhatsAppSetup />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/whatsapp-dashboard"
                element={<Navigate to="/whatsapp-hub" replace />}
              />
              <Route
                path="/meta-whatsapp-setup"
                element={
                  <ProtectedRoute>
                    <MetaWhatsAppSetup />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/whatsapp-template-approval"
                element={
                  <ProtectedRoute>
                    <WhatsAppTemplateApproval />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/whatsapp-chat"
                element={
                  <ProtectedRoute>
                    <WhatsAppChatInterface />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/email-integration"
                element={
                  <ProtectedRoute>
                    <EmailIntegration />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/campaigns"
                element={
                  <ProtectedRoute>
                    <Campaigns />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/cart-recovery"
                element={
                  <ProtectedRoute>
                    <CartRecovery />
                  </ProtectedRoute>
                }
              />
              {/* Serial/Batch tracker removed — not needed for commerce AI */}
              {/* CashTracking removed — payments captured in invoicing */}
              <Route
                path="/coupons"
                element={
                  <ProtectedRoute>
                    <CouponManager />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/vector-database"
                element={
                  <ProtectedRoute>
                    <VectorDatabase />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/settings"
                element={
                  <ProtectedRoute>
                    <Settings />
                  </ProtectedRoute>
                }
              />
              {/* UserActivation removed — handled by SuperAdmin portal */}
              <Route
                path="/brands"
                element={
                  <ProtectedRoute>
                    <Settings />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/integrations"
                element={
                  <ProtectedRoute>
                    <IntegrationForms />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/integrations-old"
                element={
                  <ProtectedRoute>
                    <Integrations />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/workflow-automation"
                element={
                  <ProtectedRoute>
                    <WorkflowAutomation />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/agents"
                element={
                  <ProtectedRoute>
                    <Agents />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/team-management"
                element={
                  <ProtectedRoute>
                    <TeamManagementPro />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/api-management"
                element={
                  <ProtectedRoute>
                    <APIManagement />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/journey-builder"
                element={
                  <ProtectedRoute>
                    <JourneyBuilder />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/lead-management"
                element={
                  <ProtectedRoute>
                    <LeadManagement />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/customer-crm"
                element={
                  <ProtectedRoute>
                    <CustomerCRM />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/customer-intelligence"
                element={
                  <ProtectedRoute>
                    <CustomerIntelligence />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/governance"
                element={
                  <ProtectedRoute>
                    <Governance />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/data-sources"
                element={
                  <ProtectedRoute>
                    <DataSources />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/ask-aicos"
                element={
                  <ProtectedRoute>
                    <AskAICOS />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/segment-builder"
                element={
                  <ProtectedRoute>
                    <SegmentBuilder />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/social-commerce"
                element={
                  <ProtectedRoute>
                    <SocialCommerce />
                  </ProtectedRoute>
                }
              />
              {/* ABTesting removed */}
              <Route
                path="/loyalty"
                element={
                  <ProtectedRoute>
                    <LoyaltyProgram />
                  </ProtectedRoute>
                }
              />
              <Route path="/order-management" element={<ProtectedRoute><OrderManagement /></ProtectedRoute>} />
              <Route path="/product-catalog" element={<ProtectedRoute><ProductCatalog /></ProtectedRoute>} />
              <Route path="/reviews" element={<ProtectedRoute><ReviewsDashboard /></ProtectedRoute>} />
              <Route path="/notification-preferences" element={<ProtectedRoute><NotificationPreferences /></ProtectedRoute>} />
              <Route path="/store-management" element={<ProtectedRoute><StoreManagement /></ProtectedRoute>} />
              <Route path="/purchase-vendor" element={<ProtectedRoute><PurchaseVendor /></ProtectedRoute>} />
              {/* Proposal Automation - Main domain with nested routes */}
              <Route
                path="/proposal-automation"
                element={
                  <ProtectedRoute>
                    <ProposalAutomation />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/proposal-automation/templates"
                element={
                  <ProtectedRoute>
                    <TemplateManagement />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/canva/callback"
                element={<CanvaCallback />}
              />
              <Route
                path="/proposal-automation/generator"
                element={
                  <ProtectedRoute>
                    <ProposalGenerator />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/integrations/shopify"
                element={
                  <ProtectedRoute>
                    <ShopifyIntegration />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/integrations/wix"
                element={
                  <ProtectedRoute>
                    <WixIntegration />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/inventory-management"
                element={
                  <ProtectedRoute>
                    <InventoryManagement />
                  </ProtectedRoute>
                }
              />
              <Route path="/customer-support-queries" element={<Navigate to="/support-center" replace />} />
              <Route path="/support" element={<Navigate to="/support-center" replace />} />
              <Route path="/support/tickets" element={<Navigate to="/support-center" replace />} />
              <Route
                path="/account-management"
                element={
                  <ProtectedRoute>
                    <AccountManagement />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/system-test"
                element={
                  <ProtectedRoute>
                    <SystemTest />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/advanced-insights"
                element={
                  <ProtectedRoute>
                    <AdvancedInsights />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/journey-orchestration"
                element={
                  <ProtectedRoute>
                    <JourneyOrchestration />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/onboarding"
                element={<Onboarding />}
              />
              <Route
                path="/faq"
                element={<FAQ />}
              />

              {/* === NEW REDESIGNED ROUTES === */}
              <Route path="/new-onboarding" element={<NewOnboarding />} />
              <Route
                path="/campaign-wizard"
                element={<ProtectedRoute><CampaignWizard /></ProtectedRoute>}
              />
              <Route
                path="/support-center"
                element={<ProtectedRoute><SupportCenter /></ProtectedRoute>}
              />
              <Route
                path="/unified-analytics"
                element={<ProtectedRoute><UnifiedAnalytics /></ProtectedRoute>}
              />
              <Route
                path="/whatsapp-hub"
                element={<ProtectedRoute><WhatsAppHub /></ProtectedRoute>}
              />
              <Route
                path="/automations"
                element={<ProtectedRoute><AutomationsHub /></ProtectedRoute>}
              />
              {/* Returns removed — handled by brand's own ecommerce backend */}
              <Route
                path="/shipping"
                element={<ProtectedRoute><ShippingTracker /></ProtectedRoute>}
              />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />

              {/* IndiaMart-parity wave 4 */}
              <Route path={m("/mela/seller/webhooks")} element={<MelaSellerWebhooks />} />
              <Route path={m("/mela/seller/analytics")} element={<MelaSellerAnalytics />} />

              <Route path="*" element={<NotFound />} />
            </Routes>
            </Suspense>
          </div>
        </Router>
      </BrandProvider>
    </AuthProvider>
    </QueryClientProvider>
    </ErrorBoundary>
    </HelmetProvider>
  );
}

export default App;